import { motion } from "framer-motion";
import {
    ArrowRight,
    BrainCircuit,
    CalendarClock,
    Copy,
    FileText,
    Folder,
    Mail,
    MessageCircle,
    Phone,
    Sparkles,
    Table2,
} from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

const TILES = [
    { icon: Table2, color: "#00E5FF", x: 4, y: 30, d: 0 },
    { icon: Mail, color: "#0066FF", x: 18, y: 12, d: 0.4 },
    { icon: FileText, color: "#7C5CFC", x: 40, y: 18, d: 0.8 },
    { icon: Phone, color: "#A855F7", x: 12, y: 62, d: 0.2 },
    { icon: Folder, color: "#D946EF", x: 34, y: 72, d: 0.6 },
    { icon: MessageCircle, color: "#22D3EE", x: 44, y: 48, d: 1 },
];

const HUB = { x: 66, y: 48 };

const Visual = () => (
    <div className="relative mt-14 h-72 sm:h-80" data-testid="challenge-visual">
        <svg aria-hidden="true" viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 h-full w-full">
            <defs>
                <linearGradient id="hubLine" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0" stopColor="#00E5FF" stopOpacity="0.15" />
                    <stop offset="1" stopColor="#A855F7" stopOpacity="0.7" />
                </linearGradient>
            </defs>
            {TILES.map((t, i) => {
                const x1 = t.x + 6;
                const y1 = t.y + 8;
                const mx = (x1 + HUB.x) / 2;
                return (
                    <path
                        key={i}
                        d={`M ${x1} ${y1} Q ${mx} ${y1 < HUB.y ? y1 + 18 : y1 - 18}, ${HUB.x} ${HUB.y}`}
                        fill="none"
                        stroke="url(#hubLine)"
                        strokeWidth="1"
                        vectorEffect="non-scaling-stroke"
                        strokeDasharray="3 4"
                    />
                );
            })}
        </svg>
        {TILES.map((t, i) => {
            const Icon = t.icon;
            return (
                <motion.div
                    key={i}
                    initial={{ opacity: 0, scale: 0.6 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.5, ease: EASE, delay: 0.1 + i * 0.08 }}
                    className="absolute"
                    style={{ left: `${t.x}%`, top: `${t.y}%` }}
                >
                    <motion.div
                        animate={{ y: [0, -6, 0] }}
                        transition={{ duration: 4 + t.d, repeat: Infinity, ease: "easeInOut", delay: t.d }}
                        className="flex h-12 w-12 items-center justify-center rounded-xl border border-white/10 bg-[#101A30] shadow-lg"
                    >
                        <Icon className="h-5 w-5" style={{ color: t.color }} strokeWidth={1.75} />
                    </motion.div>
                </motion.div>
            );
        })}
        <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.6, ease: EASE, delay: 0.5 }}
            className="absolute"
            style={{ left: `${HUB.x}%`, top: `${HUB.y}%`, transform: "translate(-50%, -50%)" }}
        >
            <div className="relative flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-[#0066FF] to-[#A855F7] shadow-[0_0_50px_rgba(0,102,255,0.5)]">
                <span className="font-display text-lg font-extrabold text-white">AI</span>
                <Sparkles className="absolute -right-1 -top-1 h-3.5 w-3.5 text-[#00E5FF]" />
                <span aria-hidden="true" className="absolute inset-0 animate-ping rounded-full border border-[#00E5FF]/30" />
            </div>
        </motion.div>
    </div>
);

const CARDS = [
    {
        num: "01",
        title: "Spreadsheets & Paperwork",
        body: "Information scattered across files, folders and paper forms. Updates are easily missed.",
        icon: Copy,
    },
    {
        num: "02",
        title: "Disconnected Scheduling",
        body: "Jobs, engineers and appointments managed separately, creating extra coordination and delays.",
        icon: CalendarClock,
    },
    {
        num: "03",
        title: "Manual Processes",
        body: "Teams repeatedly enter information, chase updates and move data between systems.",
        icon: MessageCircle,
    },
    {
        num: "04",
        title: "TedOps AI — Everything Connected",
        body: "One intelligent platform connecting people, jobs, customers and business processes, with AI-assisted insights and automation throughout.",
        icon: BrainCircuit,
        solution: true,
    },
];

const Card = ({ card, index }) => {
    const Icon = card.icon;
    const inner = (
        <div className="flex items-start gap-4 rounded-2xl bg-[#0D1526]/90 p-5 sm:p-6">
            <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${card.solution ? "bg-gradient-to-br from-[#0066FF] to-[#A855F7]" : "border border-white/10 bg-[#101A30]"}`}>
                <Icon className={`h-5 w-5 ${card.solution ? "text-white" : "text-[#22D3EE]"}`} strokeWidth={1.75} />
            </span>
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2.5">
                    <span className="font-mono-brand text-[10px] tracking-[0.2em] text-[#64748B]">{card.num}</span>
                    {card.solution && (
                        <span className="ml-auto rounded-full bg-[#00E5FF]/10 px-2.5 py-0.5 font-mono-brand text-[8px] uppercase tracking-[0.2em] text-[#00E5FF]">
                            The Solution
                        </span>
                    )}
                </div>
                <h3 className="mt-1.5 font-display text-lg font-bold leading-snug text-[#F8FAFF]">{card.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#94A3B8]">{card.body}</p>
            </div>
            <span className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-white/10 text-[#64748B] transition-colors duration-300 group-hover:border-[#00E5FF]/50 group-hover:text-[#00E5FF]">
                <ArrowRight className="h-3.5 w-3.5" />
            </span>
        </div>
    );
    return (
        <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, ease: EASE, delay: 0.1 + index * 0.1 }}
            className={`group relative ${card.solution ? "rounded-2xl bg-gradient-to-r from-[#00E5FF] via-[#0066FF] to-[#A855F7] p-[1.5px] shadow-[0_0_40px_-10px_rgba(0,102,255,0.5)]" : "rounded-2xl border border-white/10"}`}
            data-testid={`challenge-card-${index + 1}`}
        >
            {inner}
        </motion.div>
    );
};

export default function Challenge() {
    return (
        <section
            className="relative overflow-hidden bg-[#0A0F1C]"
            data-testid="challenge-section"
            aria-labelledby="challenge-heading"
        >
            <div
                aria-hidden="true"
                className="absolute inset-0"
                style={{
                    background:
                        "radial-gradient(circle at 45% 55%, rgba(0,102,255,0.09) 0%, transparent 40%), radial-gradient(circle at 95% 90%, rgba(168,85,247,0.07) 0%, transparent 45%)",
                }}
            />
            <div className="relative mx-auto max-w-6xl px-6 py-24 sm:py-32">
                <div className="grid items-start gap-16 lg:grid-cols-2 lg:gap-14">
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.7, ease: EASE }}
                    >
                        <span
                            className="inline-flex items-center gap-2 rounded-full border border-[#00E5FF]/25 bg-[#00E5FF]/5 px-4 py-1.5"
                            data-testid="challenge-eyebrow"
                        >
                            <span className="h-1.5 w-1.5 rounded-full bg-[#00E5FF]" />
                            <span className="font-mono-brand text-[10px] uppercase tracking-[0.3em] text-[#7DD3FC] sm:text-xs">
                                The Challenge
                            </span>
                        </span>
                        <h2
                            id="challenge-heading"
                            className="mt-6 font-display text-4xl font-bold leading-[1.12] tracking-tight text-[#F8FAFF] sm:text-5xl"
                            data-testid="challenge-heading"
                        >
                            Disconnected Systems.
                            <br />
                            <span className="text-brand-gradient">Connected Intelligence.</span>
                        </h2>
                        <p className="mt-6 max-w-lg text-base leading-relaxed text-[#94A3B8]" data-testid="challenge-intro">
                            Many businesses rely on multiple systems, spreadsheets, emails and phone
                            calls to manage everyday operations. TedOps AI brings your teams,
                            workflows and information together, using intelligent automation and
                            insights to help your business run smarter.
                        </p>
                        <Visual />
                    </motion.div>

                    <div className="relative flex flex-col gap-5 lg:pt-4">
                        <div
                            aria-hidden="true"
                            className="absolute -left-5 bottom-8 top-8 hidden w-px bg-gradient-to-b from-[#00E5FF]/40 via-[#0066FF]/40 to-[#A855F7]/40 lg:block"
                        />
                        {CARDS.map((card, i) => (
                            <div key={card.num} className="relative">
                                <span
                                    aria-hidden="true"
                                    className="absolute -left-[26px] top-1/2 hidden h-2 w-2 -translate-y-1/2 rounded-full bg-[#00E5FF] shadow-[0_0_10px_rgba(0,229,255,0.8)] lg:block"
                                />
                                <Card card={card} index={i} />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
